(ns practitest.frontend.components2.dashboard.charts.line.chart
  (:require ["d3" :as d3]
            [practitest.frontend.components2.dashboard.charts.line.render-axis :as render-axis]
            [practitest.frontend.components2.dashboard.charts.line.scales :as scales]
            [practitest.frontend.components2.dashboard.charts.line.render-lines :as render-lines]
            [practitest.frontend.components2.dashboard.charts.line.pointer-events :as pointer-events]
            [practitest.frontend.components2.dashboard.charts.render-helpers :as rh]
            [practitest.frontend.common.i18n :refer [get-translation]]))

(defn- render-comment! [svg chart]
  (let [{:keys [width]
         :config/keys [height comment-top-offset comment-text]} chart
        grp (rh/ensure-group! svg "line-chart-comment")]
    (if (seq comment-text)
      (let [comment   (str "*"(apply get-translation comment-text))
            comment-y (+ height comment-top-offset)
            font-size (if (< width 350) "8px" "12px")
            text-sel  (-> grp (.selectAll "text") (.data #js [comment]))
            entered   (-> (.enter text-sel)
                          (.append "text")
                          (.attr "class" "line-chart-comment-text")
                          (.attr "text-anchor" "middle"))
            all       (.merge entered text-sel)]
        (-> all
            (.attr "x" (/ width 2))
            (.attr "y" comment-y)
            (.style "font-size" font-size)
            (.text (fn [^js d] d)))
        (-> (.exit text-sel) (.remove)))
      (-> grp (.selectAll "text") (.remove)))))

(defn- render-legend!
  "Render clickable legend items directly inside the SVG, below the chart area."
  [svg chart lines hidden-lines on-legend-click]
  (let [{:keys [width]
         :config/keys [height padding legend-item-width legend-top-offset comment-top-offset]} chart
        n            (count lines)
        inner-width  (- width (:left padding) (:right padding))
        frame-pad-x  10
        line-len     16
        legend-y     (+ height legend-top-offset comment-top-offset)
        key-fn       (fn [^js d] (aget d "line-name"))
        js-lines     (clj->js (map (fn [l]
                                     #js {:line-name  (:line-name l)
                                          :line-color (:line-color l)
                                          :hidden     (contains? (or hidden-lines #{}) (:line-name l))})
                                   lines))
        total-width  (* legend-item-width n)
        legend-width (+ total-width frame-pad-x)
        offset       (+ (:left padding) (/ (- inner-width legend-width) 2))
        legend-g     (rh/ensure-group! svg "line-legend")
        _            (.attr legend-g "transform" (str "translate(" offset "," legend-y ")"))

        frame-sel    (-> legend-g
                         (.selectAll "rect.line-legend-frame")
                         (.data #js [nil]))
        frame        (-> (.enter frame-sel)
                         (.append "rect")
                         (.attr "class" "line-legend-frame")
                         (.merge frame-sel))
        _            (-> frame
                         (.attr "x" 0)
                         (.attr "y" -14)
                         (.attr "width" legend-width)
                         (.attr "height" 28)
                         (.attr "fill" "none")
                         (.attr "stroke" "var(--pt-color-border-light)")
                         (.attr "rx" 5)
                         (.attr "ry" 5)
                         (.attr "stroke-width" 0.5))

        items        (-> legend-g
                         (.selectAll "g.line-legend-item")
                         (.data js-lines key-fn))
        entered      (-> (.enter items)
                         (.append "g")
                         (.attr "class" "line-legend-item")
                         (.attr "transform" (fn [_ i] (str "translate(" (+ frame-pad-x (* i legend-item-width)) ",0)")))
                         (.each (fn [^js d]
                                  (this-as this
                                    (let [g (.select d3 this)]
                                      (-> g
                                          (.append "circle")
                                          (.attr "cx" (/ line-len 2))
                                          (.attr "cy" 0)
                                          (.attr "r" 4)
                                          (.attr "fill" (aget d "line-color")))
                                      (-> g
                                          (.append "text")
                                          (.attr "class" "line-legend-text")
                                          (.attr "x" (+ line-len 4))
                                          (.attr "y" 4)
                                          (.text (aget d "line-name"))))))))
        all          (.merge entered items)]
    (.attr all "opacity" (fn [^js d] (if (aget d "hidden") 0.4 1)))
    (.attr all "transform" (fn [_ i] (str "translate(" (+ frame-pad-x (* i legend-item-width) 4) ",0)")))
    (-> (.exit items) (.remove))
    (.on all "click" (fn [^js _e ^js d]
                       (on-legend-click (aget d "line-name"))))))

(defn line-chart!
  "Render line chart directly into an SVG element.
   Compatible with the p/render! protocol (like bar/pie)."
    [^js svg-element {:chart/keys [data] :as data-chart} ctx]
     (when svg-element
    (let [rect         (.getBoundingClientRect svg-element)
          width        (let [w (.-width rect)] (if (pos? w) w 600))
          lines        (or data [])
          hidden-lines-val (:hidden-lines ctx)
          hidden-lines (if (fn? hidden-lines-val)
                         (hidden-lines-val data-chart lines)
                         (or hidden-lines-val #{}))
          chart        (assoc ctx :width width)
          svg          (-> (.select d3 svg-element)
                           (.attr "preserveAspectRatio" "xMidYMin meet")
                           (.attr "viewBox" (str "0 0 " width " " (:config/height chart))))
          visible      (filterv #(not (contains? hidden-lines (:line-name %))) lines)
          ;; persist scales on the SVG element itself so toggling all lines off
          ;; doesn't collapse the axes
          prev-scales  (.-__lineScales svg-element)
          scales       (if (seq visible)
                         (let [s (scales/get-scales chart visible)]
                           (set! (.-__lineScales svg-element) s)
                           s)
                         prev-scales)]
      (when scales
        (render-axis/render-axis! svg chart lines scales hidden-lines)
        (render-lines/render-lines! svg chart lines scales hidden-lines)
        (pointer-events/add-pointer-events! svg chart lines scales (:tooltip ctx))
        (when (:config/comment-text chart) (render-comment! svg chart))
        (when (:config/show-legend? chart) (render-legend! svg chart lines hidden-lines (:on-legend-click ctx)))))))